import type { BackofficeVoucherProductListItem } from '@diamond/shared/types';
import { demagnifyQuantity } from '@diamond/shared/utils';

import type {
  CreateVoucherRequest,
  ProductSelectOption,
  VoucherDetailResponse,
} from '../types';

// Extended product item type to include division from API response
type ProductItemWithDivision = BackofficeVoucherProductListItem & {
  division?: string;
};

/**
 * Format product item to ProductSelectOption with full label and division
 */
export function formatProductOption(
  item: BackofficeVoucherProductListItem
): ProductSelectOption {
  const brandName = item.brand_name || item.brand_desc || '-';
  const itemWithDivision = item as ProductItemWithDivision;
  return {
    label: `${item.item_code} - ${item.title} (${brandName})`,
    value: String(item.item_code),
    division: itemWithDivision.division,
  };
}

/**
 * Maps voucher detail API response to form data (synchronous part only)
 * Product details should be fetched separately using fetchProductDetailsForVoucher
 */
export function mapVoucherDetailToForm(
  data: VoucherDetailResponse
): CreateVoucherRequest {
  return {
    banner_url: data.banner_url ?? undefined,
    title: data.title,
    code: data.code,
    desc: data.desc ?? undefined,
    period_start_date: data.period_start_date,
    period_end_date: data.period_end_date,
    target_configuration: {
      ...data.target_configuration,
      shipto_csv_url: data.target_configuration.shipto_csv_url ?? null,
    },
    acquisition_configuration: {
      ...data.acquisition_configuration,
      valid_day_since_account_signup:
        data.acquisition_configuration.valid_day_since_account_signup ?? null,
    },
    condition_configuration: {
      ...data.condition_configuration,
    },
    reward_configuration: {
      ...data.reward_configuration,
      // Demagnify quantities from API for form display
      min_total_quantity_all_item: demagnifyQuantity(
        data.reward_configuration.min_total_quantity_all_item
      ),
      reward_rule_products: data.reward_configuration.reward_rule_products.map(
        (product) => ({
          ...product,
          min_quantity: demagnifyQuantity(product.min_quantity),
        })
      ),
      free_goods:
        data.reward_configuration.free_goods?.map((freeGood) => ({
          ...freeGood,
          quantity: demagnifyQuantity(freeGood.quantity) ?? freeGood.quantity,
        })) ?? [],
      // Add form-specific helper fields
      discount_amount_value:
        data.reward_configuration?.discount_products?.[0]?.discount_amount ??
        null,
      discount_percentage_value:
        data.reward_configuration?.discount_products?.[0]
          ?.discount_percentage ?? null,
      free_goods_quantity_value: demagnifyQuantity(
        data.reward_configuration?.free_goods?.[0]?.quantity
      ),
    },
    // Product fields will be populated after fetching details
    purchaseProducts: [],
    rewardDiscountProduct: null,
    rewardFreeProduct: null,
    // Mark code as validated since it's from existing voucher
    codeValidated: true,
  };
}
